import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import '../inventory.css';
import './ProductForm.css';

/**
 * ProductOverview Component
 * 
 * Dashboard-style view showing summary of ALL relationships for a selected product
 * Provides quick insights into:
 * - Stock levels across warehouses
 * - Active reservations
 * - Recent movements
 * - Price lists
 * - Suppliers
 * - Variants
 * - Orders (purchase, sales, returns)
 * - Transfers and counts
 */
const ProductOverview = ({ organization = 'TestOrg' }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetails, setProductDetails] = useState(null);
  const [overview, setOverview] = useState({
    stock: [],
    reservations: [],
    movements: [],
    priceLists: [],
    suppliers: [],
    variants: [],
    purchaseOrders: 0,
    salesOrders: 0,
    returnOrders: 0,
    transfers: 0,
    cycleCounts: 0,
    valuations: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadProducts();
  }, [organization]);

  useEffect(() => {
    if (selectedProduct) {
      loadProductOverview();
    }
  }, [selectedProduct]);

  const loadProducts = async () => {
    try {
      const response = await inventoryApi.getProducts(organization);
      const productsData = Array.isArray(response) ? response : (response.data || []);
      setProducts(productsData);
      if (productsData.length > 0) {
        setSelectedProduct(productsData[0].id);
      }
    } catch (err) {
      setError(err.message || 'Failed to load products');
    }
  };

  const loadProductOverview = async () => {
    if (!selectedProduct) return;

    try {
      setLoading(true);
      setError(null);

      // Get product details
      const product = products.find(p => p.id === selectedProduct);
      setProductDetails(product);

      // Load all relationship data in parallel
      const [
        stockRes,
        reservationsRes,
        movementsRes,
        priceListsRes,
        suppliersRes,
        variantsRes,
        valuationsRes
      ] = await Promise.all([
        inventoryApi.getProductStock(organization, selectedProduct).catch(() => ({ data: [] })),
        inventoryApi.getStockReservations(organization, selectedProduct).catch(() => ({ data: [] })),
        inventoryApi.getStockMovements(organization, { productId: selectedProduct }).catch(() => ({ data: [] })),
        inventoryApi.getProductPriceLists(selectedProduct).catch(() => ({ data: [] })),
        inventoryApi.getProductSuppliers(selectedProduct).catch(() => ({ data: [] })),
        inventoryApi.getProductVariants(selectedProduct).catch(() => ({ data: [] })),
        inventoryApi.getInventoryValuations(organization).catch(() => ({ data: [] }))
      ]);

      const stock = Array.isArray(stockRes) ? stockRes : (stockRes.data || []);
      const reservations = Array.isArray(reservationsRes) ? reservationsRes : (reservationsRes.data || []);
      const movements = Array.isArray(movementsRes) ? movementsRes : (movementsRes.data || []);
      const priceLists = Array.isArray(priceListsRes) ? priceListsRes : (priceListsRes.data || []);
      const suppliers = Array.isArray(suppliersRes) ? suppliersRes : (suppliersRes.data || []);
      const variants = Array.isArray(variantsRes) ? variantsRes : (variantsRes.data || []);
      const valuations = Array.isArray(valuationsRes) ? valuationsRes : (valuationsRes.data || []);

      setOverview({
        stock,
        reservations: reservations.filter(r => (r.status || '').toLowerCase() === 'active'),
        movements: movements.slice(0, 10), // Last 10 movements
        priceLists,
        suppliers,
        variants,
        purchaseOrders: 0, // Would need to count from orders
        salesOrders: 0,
        returnOrders: 0,
        transfers: 0,
        cycleCounts: 0,
        valuations: valuations.filter(v => (v.product_id || v.productId) === selectedProduct)
      });
    } catch (err) {
      setError(err.message || 'Failed to load product overview');
    } finally {
      setLoading(false);
    }
  };

  const getTotalStock = () => {
    return overview.stock.reduce((sum, s) => 
      sum + parseFloat(s.quantity_on_hand || s.quantityOnHand || 0), 0
    );
  };

  const getTotalAvailable = () => {
    return overview.stock.reduce((sum, s) => 
      sum + parseFloat(s.quantity_available || s.quantityAvailable || 0), 0
    );
  };

  const getTotalReserved = () => {
    return overview.reservations.reduce((sum, r) => 
      sum + parseFloat(r.quantity_reserved || r.quantityReserved || 0), 0
    );
  };

  const getCurrentValue = () => {
    if (overview.valuations.length === 0) return 0;
    const latest = overview.valuations[overview.valuations.length - 1];
    return parseFloat(latest.total_value || latest.totalValue || 0);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && !productDetails) {
    return (
      <div className="pm-page-card pm-skeleton" aria-busy="true">
        {t('inventory.loading_product_overview')}
      </div>
    );
  }

  return (
    <div className="pm-page-card">
      <div className="pm-header">
        <h2 className="pm-header-title">{t('inventory.product_overview_title')}</h2>
      </div>

      {error && (
        <div className="pm-error-banner">
          <span aria-hidden="true">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      <div className="pm-toolbar">
        <div className="pm-search-wrap">
          <select 
            value={selectedProduct || ''} 
            onChange={(e) => setSelectedProduct(Number(e.target.value))}
          >
            <option value="">{t('inventory.select_product_option')}</option>
            {products.map(product => (
              <option key={product.id} value={product.id}>
                {product.name} ({product.sku || product.SKU || 'N/A'})
              </option>
            ))}
          </select>
        </div>
      </div>

      {productDetails && (
        <>
          <div className="pm-hero-card">
            <div className="pm-hero-info">
              <div>
                <h2>{productDetails.name}</h2>
                <p className="pm-hero-meta">
                  {t('inventory.sku_label')} {productDetails.sku || productDetails.SKU || t('inventory.na')} | 
                  {t('inventory.type_label')} {productDetails.product_type || productDetails.productType || t('inventory.na')} | 
                  {t('inventory.category_label')} {productDetails.category || t('inventory.na')}
                </p>
              </div>
              <div className="pm-hero-pricing">
                <div className="pm-price-item">
                  <span className="pm-price-label">{t('inventory.unit_price_label')}</span>
                  <span className="pm-price-value">{formatCurrency(productDetails.unit_price || productDetails.unitPrice)}</span>
                </div>
                <div className="pm-price-item">
                  <span className="pm-price-label">{t('inventory.cost_label')}</span>
                  <span className="pm-price-value">{formatCurrency(productDetails.cost)}</span>
                </div>
              </div>
            </div>
            {productDetails.description && (
              <p className="pm-hero-description">{productDetails.description}</p>
            )}
          </div>

          <div className="pm-metric-grid">
            <div className="pm-metric-card">
              <h3>{t('inventory.inventory_heading')}</h3>
              <div className="pm-metric-inner">
                <div className="pm-metric-item">
                  <span className="pm-metric-label">{t('inventory.on_hand_metric')}</span>
                  <span className="pm-metric-value">{getTotalStock().toFixed(2)}</span>
                </div>
                <div className="pm-metric-item">
                  <span className="pm-metric-label">{t('inventory.available_metric')}</span>
                  <span className="pm-metric-value available">{getTotalAvailable().toFixed(2)}</span>
                </div>
                <div className="pm-metric-item">
                  <span className="pm-metric-label">{t('inventory.reserved_metric')}</span>
                  <span className="pm-metric-value reserved">{getTotalReserved().toFixed(2)}</span>
                </div>
                <div className="pm-metric-item">
                  <span className="pm-metric-label">{t('inventory.locations_metric')}</span>
                  <span className="pm-metric-value">{overview.stock.length}</span>
                </div>
              </div>
            </div>

            <div className="pm-metric-card">
              <h3>{t('inventory.product_data_heading')}</h3>
              <div className="pm-metric-inner">
                <div className="pm-metric-item">
                  <span className="pm-metric-label">{t('inventory.variants_metric')}</span>
                  <span className="pm-metric-value">{overview.variants.length}</span>
                </div>
                <div className="pm-metric-item">
                  <span className="pm-metric-label">{t('inventory.price_lists_metric')}</span>
                  <span className="pm-metric-value">{overview.priceLists.length}</span>
                </div>
                <div className="pm-metric-item">
                  <span className="pm-metric-label">{t('inventory.suppliers_metric')}</span>
                  <span className="pm-metric-value">{overview.suppliers.length}</span>
                </div>
                <div className="pm-metric-item">
                  <span className="pm-metric-label">{t('inventory.current_value_metric')}</span>
                  <span className="pm-metric-value">{formatCurrency(getCurrentValue())}</span>
                </div>
              </div>
            </div>

            <div className="pm-metric-card">
              <h3>{t('inventory.activity_heading')}</h3>
              <div className="pm-metric-inner">
                <div className="pm-metric-item">
                  <span className="pm-metric-label">{t('inventory.active_reservations_metric')}</span>
                  <span className="pm-metric-value">{overview.reservations.length}</span>
                </div>
                <div className="pm-metric-item">
                  <span className="pm-metric-label">{t('inventory.recent_movements_metric')}</span>
                  <span className="pm-metric-value">{overview.movements.length}</span>
                </div>
                <div className="pm-metric-item">
                  <span className="pm-metric-label">{t('inventory.purchase_orders_metric')}</span>
                  <span className="pm-metric-value">{overview.purchaseOrders}</span>
                </div>
                <div className="pm-metric-item">
                  <span className="pm-metric-label">{t('inventory.sales_orders_metric')}</span>
                  <span className="pm-metric-value">{overview.salesOrders}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="pm-detail-grid">
            <div className="pm-detail-section">
              <h3>{t('inventory.stock_by_location_heading')} ({overview.stock.length})</h3>
              {overview.stock.length === 0 ? (
                <div className="pm-empty-state">
                  <div className="pm-empty-state-icon" aria-hidden="true">📦</div>
                  <p>{t('inventory.no_stock_records')}</p>
                </div>
              ) : (
                <div className="pm-table-wrap">
                  <table className="pm-table" role="grid">
                    <thead>
                      <tr>
                        <th>{t('inventory.warehouse_header')}</th>
                        <th>{t('inventory.location_header')}</th>
                        <th>{t('inventory.on_hand_metric')}</th>
                        <th>{t('inventory.available_metric')}</th>
                        <th>{t('inventory.lot_number_header')}</th>
                        <th>{t('inventory.expiry_header')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.stock.slice(0, 5).map((s, idx) => (
                        <tr key={idx}>
                          <td>{s.warehouse_name || s.warehouseName || t('inventory.na')}</td>
                          <td>{s.location_id || s.locationId || t('inventory.na')}</td>
                          <td>{parseFloat(s.quantity_on_hand || s.quantityOnHand || 0).toFixed(2)}</td>
                          <td>{parseFloat(s.quantity_available || s.quantityAvailable || 0).toFixed(2)}</td>
                          <td>{s.lot_number || s.lotNumber || '-'}</td>
                          <td>{formatDate(s.expiration_date || s.expirationDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="pm-detail-section">
              <h3>{t('inventory.recent_movements_heading')} ({overview.movements.length})</h3>
              {overview.movements.length === 0 ? (
                <div className="pm-empty-state">
                  <div className="pm-empty-state-icon" aria-hidden="true">📈</div>
                  <p>{t('inventory.no_recent_movements')}</p>
                </div>
              ) : (
                <div className="pm-table-wrap">
                  <table className="pm-table" role="grid">
                    <thead>
                      <tr>
                        <th>{t('inventory.date_header')}</th>
                        <th>{t('inventory.type_header')}</th>
                        <th>{t('inventory.quantity_header')}</th>
                        <th>{t('inventory.reference_header')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.movements.slice(0, 5).map((m, idx) => (
                        <tr key={idx}>
                          <td>{formatDate(m.movement_date || m.movementDate)}</td>
                          <td>{(m.movement_type || m.movementType || t('inventory.na')).toUpperCase()}</td>
                          <td>{parseFloat(m.quantity || 0).toFixed(2)}</td>
                          <td>{m.reference_number || m.referenceNumber || t('inventory.na')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="pm-detail-section">
              <h3>{t('inventory.suppliers_heading')} ({overview.suppliers.length})</h3>
              {overview.suppliers.length === 0 ? (
                <div className="pm-empty-state">
                  <div className="pm-empty-state-icon" aria-hidden="true">🏭</div>
                  <p>{t('inventory.no_suppliers')}</p>
                </div>
              ) : (
                <div className="pm-table-wrap">
                  <table className="pm-table" role="grid">
                    <thead>
                      <tr>
                        <th>{t('inventory.supplier_header')}</th>
                        <th>{t('inventory.supplier_sku_header')}</th>
                        <th>{t('inventory.unit_price_label')}</th>
                        <th>{t('inventory.lead_time_header')}</th>
                        <th>{t('inventory.preferred_header')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.suppliers.map((s, idx) => (
                        <tr key={idx}>
                          <td>{s.supplier_name || s.supplierName || t('inventory.na')}</td>
                          <td>{s.supplier_sku || s.supplierSku || t('inventory.na')}</td>
                          <td>{formatCurrency(s.unit_price || s.unitPrice)}</td>
                          <td>{s.lead_time_days || s.leadTimeDays || 0} {t('inventory.days_suffix')}</td>
                          <td>{(s.is_preferred || s.isPreferred) ? '⭐' : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="pm-detail-section">
              <h3>{t('inventory.active_reservations_heading')} ({overview.reservations.length})</h3>
              {overview.reservations.length === 0 ? (
                <div className="pm-empty-state">
                  <div className="pm-empty-state-icon" aria-hidden="true">🔒</div>
                  <p>{t('inventory.no_active_reservations')}</p>
                </div>
              ) : (
                <div className="pm-table-wrap">
                  <table className="pm-table" role="grid">
                    <thead>
                      <tr>
                        <th>{t('inventory.reservation_type_header')}</th>
                        <th>{t('inventory.quantity_header')}</th>
                        <th>{t('inventory.reference_header')}</th>
                        <th>{t('inventory.reserved_date_header')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.reservations.slice(0, 5).map((r, idx) => (
                        <tr key={idx}>
                          <td>{(r.reservation_type || r.reservationType || t('inventory.na')).replace('_', ' ').toUpperCase()}</td>
                          <td>{parseFloat(r.quantity_reserved || r.quantityReserved || 0).toFixed(2)}</td>
                          <td>{r.reference_number || r.referenceNumber || t('inventory.na')}</td>
                          <td>{formatDate(r.reserved_date || r.reservedDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductOverview;
